import React, { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { errorMessage } from "@/lib/errors";
import { Loader2, Check, Sparkles, Coins, BadgePercent } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { Link, useSearchParams } from "react-router-dom";

const BUNDLES = [
  {
    key: "match_reveal",
    name: "Single reveal",
    price: "$10",
    per: "one-time",
    each: "$10 per reveal",
    desc: "Unlock the match on the checkout screen — no credits, no commitment.",
    bullets: ["Instant, one specific match", "No account balance", "Non-recurring"],
    cta: "Buy 1 reveal",
    savings: null,
  },
  {
    key: "reveal_pack_3",
    name: "Reveal Pack — 3",
    price: "$25.99",
    per: "one-time",
    each: "$8.66 per reveal",
    desc: "3 portable credits. Spend them on any future match with one tap.",
    bullets: ["3 reveal credits", "Save 13%", "Never expire"],
    cta: "Buy 3 credits",
    savings: "Save 13%",
    featured: true,
  },
  {
    key: "reveal_pack_10",
    name: "Reveal Pack — 10",
    price: "$69.99",
    per: "one-time",
    each: "$7.00 per reveal",
    desc: "10 portable credits. Best value if you're actively dating.",
    bullets: ["10 reveal credits", "Save 30%", "Never expire"],
    cta: "Buy 10 credits",
    savings: "Best value · Save 30%",
  },
  {
    key: "streak_freeze",
    name: "Streak Freeze",
    price: "$0.99",
    per: "one token",
    each: "Save your streak on off days",
    desc: "One token protects your streak from a single missed day. Consumed automatically.",
    bullets: ["Auto-applied", "Never expires", "Stack multiple tokens"],
    cta: "Buy 1 freeze",
    savings: null,
  },
];

const MEMBERSHIPS = [
  {
    key: "prime_monthly",
    name: "Prime Monthly",
    price: "$19.99",
    per: "per month",
    desc: "Unlimited match reveals + priority visibility. Cancel anytime.",
    bullets: ["Unlimited reveals", "Priority in search", "Cancel anytime"],
    cta: "Start monthly",
    featured: true,
  },
  {
    key: "prime_yearly",
    name: "Prime Yearly",
    price: "$144",
    per: "per year",
    desc: "Best value for members who plan to browse for a while.",
    bullets: ["Everything in Monthly", "40% cheaper than monthly", "One payment"],
    cta: "Start yearly",
  },
];

function PlanCard({ p, onBuy, busy, featured }) {
  const cls = featured
    ? "bg-[#722f37] text-white border-[#722f37]"
    : "bg-white border-[#e5e0d8]";
  return (
    <div className={`p-8 rounded-2xl border card-elev flex flex-col ${cls}`} data-testid={`pricing-card-${p.key}`}>
      <div className={`text-sm uppercase tracking-[0.22em] font-bold ${featured ? "text-[#e5c65b]" : "text-[#722f37]"}`}>
        {p.name}
      </div>
      {p.savings && (
        <div className={`mt-2 text-xs font-semibold ${featured ? "text-[#e5c65b]" : "text-[#c39d2b]"}`} data-testid={`pricing-savings-${p.key}`}>
          {p.savings}
        </div>
      )}
      <div className="flex items-baseline gap-2 mt-4">
        <div className="font-serif-display text-5xl">{p.price}</div>
        <div className={`text-sm ${featured ? "text-white/70" : "text-[#5c5c5c]"}`}>{p.per}</div>
      </div>
      {p.each && (
        <div className={`mt-1 text-xs ${featured ? "text-white/70" : "text-[#5c5c5c]"}`}>{p.each}</div>
      )}
      <p className={`mt-4 ${featured ? "text-white/85" : "text-[#5c5c5c]"}`}>{p.desc}</p>
      <ul className={`mt-6 space-y-2 text-sm ${featured ? "text-white/90" : "text-[#1a1a1a]"}`}>
        {p.bullets.map((b) => (
          <li key={b} className="flex items-start gap-2">
            <Check className={`w-4 h-4 mt-0.5 ${featured ? "text-[#e5c65b]" : "text-[#722f37]"}`} /> {b}
          </li>
        ))}
      </ul>
      <div className="mt-auto pt-8">
        <button
          onClick={() => onBuy(p.key)}
          disabled={busy === p.key}
          className={featured ? "btn-gold w-full flex items-center justify-center gap-2" : "btn-primary w-full flex items-center justify-center gap-2"}
          data-testid={`pricing-cta-${p.key}`}
        >
          {busy === p.key ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          {p.cta}
        </button>
      </div>
    </div>
  );
}

export default function Pricing() {
  const { user } = useAuth();
  const [busyKey, setBusyKey] = useState(null);
  const [searchParams] = useSearchParams();
  const promo = searchParams.get("promo") || "";

  const buy = async (lookup_key) => {
    if (lookup_key === "match_reveal") {
      toast.info("Single reveals are purchased from the specific profile card.");
      return;
    }
    setBusyKey(lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key,
        origin_url: window.location.origin,
        promo_code: promo || undefined,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(errorMessage(err, "Could not start checkout"));
      setBusyKey(null);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-14 md:py-20">
      <div className="max-w-2xl">
        <span className="text-sm uppercase tracking-[0.28em] font-bold text-[#722f37]">Pricing</span>
        <h1 className="font-serif-display text-4xl md:text-5xl mt-3">Pay only for the matches you love.</h1>
        <p className="mt-4 text-lg text-[#5c5c5c]">
          Buy reveal credits in bundles — the more you buy, the less you pay per match — or go unlimited with membership.
        </p>
      </div>

      {user && (
        <div className="mt-8 glass rounded-2xl px-5 py-4 inline-flex items-center gap-3" data-testid="credits-balance">
          <Coins className="w-5 h-5 text-[#c39d2b]" />
          <div>
            <div className="text-xs uppercase tracking-[0.22em] font-bold text-[#722f37]">Your balance</div>
            <div className="text-lg font-medium">
              {user.subscription_active ? (
                <span className="text-[#722f37]">Unlimited (membership active)</span>
              ) : (
                <span>{user.reveal_credits || 0} reveal credit{(user.reveal_credits || 0) === 1 ? "" : "s"}</span>
              )}
            </div>
          </div>
        </div>
      )}

      {promo && (
        <div
          className="mt-6 rounded-2xl bg-[#c39d2b]/15 border border-[#c39d2b]/40 px-5 py-4 flex items-center gap-3 max-w-xl"
          data-testid="promo-banner"
        >
          <BadgePercent className="w-6 h-6 text-[#8a6a10]" />
          <div>
            <div className="text-sm font-bold text-[#8a6a10]">Promo code <span className="uppercase">{promo}</span> applied</div>
            <div className="text-xs text-[#5c5c5c]">Discount is calculated at Stripe checkout on eligible items.</div>
          </div>
        </div>
      )}

      <section className="mt-14">
        <div className="flex items-baseline justify-between flex-wrap gap-4">
          <h2 className="font-serif-display text-3xl md:text-4xl">Reveal bundles</h2>
          <span className="text-sm text-[#5c5c5c]">One-time · credits never expire</span>
        </div>
        <div className="grid md:grid-cols-3 gap-8 mt-8">
          {BUNDLES.map((p) => (
            <PlanCard key={p.key} p={p} onBuy={buy} busy={busyKey} featured={p.featured} />
          ))}
        </div>
      </section>

      <section className="mt-20">
        <div className="flex items-baseline justify-between flex-wrap gap-4">
          <h2 className="font-serif-display text-3xl md:text-4xl">Prime Membership</h2>
          <span className="text-sm text-[#5c5c5c]">Unlimited reveals · priority visibility</span>
        </div>
        <div className="grid md:grid-cols-2 gap-8 mt-8 max-w-4xl">
          {MEMBERSHIPS.map((p) => (
            <PlanCard key={p.key} p={p} onBuy={buy} busy={busyKey} featured={p.featured} />
          ))}
        </div>
      </section>

      <div className="mt-16 flex items-center gap-3 text-sm text-[#5c5c5c]">
        <Sparkles className="w-4 h-4 text-[#c39d2b]" />
        Payments powered by Stripe. Test card <code className="px-1 rounded bg-[#eae5d9]">4242 4242 4242 4242</code>, any future expiry, any CVC.
      </div>

      <div className="mt-4 text-sm text-[#5c5c5c]">
        <Link to="/matches" className="text-[#722f37] font-medium">Back to matches</Link>
      </div>
    </div>
  );
}
